import React, { useState, useEffect } from "react";
import { Box, Button, Paper, Typography, Fade } from "@mui/material";
import { styled, keyframes } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BackspaceIcon from "@mui/icons-material/Backspace";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import { isAnswerAccurate } from "../../../../utils/answerAccuracy";
import AudioPlayer from "./AudioPlayer";

// Core animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Application theme colors
const appTheme = {
  primary: {
    main: "rgb(0, 17, 130)",
    light: "rgb(48, 46, 95)",
    dark: "rgb(3, 11, 166)",
    contrastText: "#ffffff"
  },
  secondary: {
    main: "#FF4B4B",
    light: "#FF7676",
    dark: "#D33131",
    contrastText: "#ffffff"
  },
  success: {
    main: "#58CC02",
    light: "rgb(89, 249, 139)",
    dark: "rgb(3, 166, 30)",
    background: "#E9FAD3"
  },
  error: {
    main: "#FF4B4B",
    light: "#FF7676",
    dark: "#D33131",
    background: "#FFEBEB"
  },
  neutral: {
    light: "#F7F7F7",
    main: "#E5E5E5",
    dark: "#AFAFAF",
    darker: "#777777"
  },
  blue: {
    main: "#1CB0F6",
    light: "#58C2F1",
    dark: "#0095D8",
  }
};

// Styled token component with increased height
const TokenCard = styled(Paper)(({ selected, isCorrect, isIncorrect, isVertical }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: isVertical ? "10px 14px" : "8px 16px", // Increased padding for height
  margin: "4px",
  borderRadius: "10px",
  cursor: selected ? "default" : "pointer",
  position: "relative",
  fontWeight: "700",
  fontSize: "14px",
  lineHeight: "1.2",
  color: selected ? '#fff' : "#3C3C3C",
  maxHeight: isVertical ? "auto" : "42px", // Increased height
  backgroundColor: isCorrect 
    ? appTheme.success.dark // Changed to dark green for correct tokens
    : isIncorrect 
      ? appTheme.error.main
      : selected 
        ? appTheme.primary.main
        : "#ffffff",
  border: selected 
    ? "none"
    : `2px solid ${appTheme.neutral.main}`,
  boxShadow: selected 
    ? "0 2px 0 rgba(0,0,0,0.2)"
    : "0 2px 0 rgba(0,0,0,0.1)",
  transition: "all 0.15s ease-in-out",
  "&:hover": {
    boxShadow: selected 
      ? "0 2px 0 rgba(0,0,0,0.2)"
      : "0 4px 0 rgba(0,0,0,0.15)",
    transform: selected ? "none" : "translateY(-2px)",
    backgroundColor: selected 
      ? (isCorrect ? appTheme.success.dark : isIncorrect ? appTheme.error.main : appTheme.primary.main)
      : "#fafafa",
  },
  "&:active": {
    transform: selected ? "none" : "translateY(1px)",
    boxShadow: "0 1px 0 rgba(0,0,0,0.1)",
  },
  minWidth: "40px",
  minHeight: isVertical ? "auto" : "40px", // Increased min height
  width: isVertical ? "100%" : "auto",
  animation: `${popIn} 0.3s ease forwards`,
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

// Enhanced hotkey indicator
const Hotkey = styled(Box)({
  position: "absolute",
  top: "2px",
  right: "4px",
  fontSize: "9px",
  fontWeight: "800",
  color: "rgba(0,0,0,0.5)",
  backgroundColor: "rgba(255,255,255,0.8)",
  borderRadius: "3px",
  padding: "0px 3px",
  lineHeight: "1.4",
});

// Container for ordered tokens
const OrderedContainer = styled(Box)(({ isCorrect, isIncorrect, isVertical, isEmpty }) => ({
  minHeight: "60px",
  padding: "14px",
  marginBottom: "16px",
  borderRadius: "16px",
  backgroundColor: isCorrect 
    ? "rgba(88, 204, 2, 0.1)" // Light green background for correct
    : isIncorrect 
      ? "rgba(255, 75, 75, 0.1)" // Light red background for incorrect
      : "#FFFFFF",
  border: isEmpty
    ? `2px dashed ${appTheme.neutral.main}`
    : isCorrect 
      ? `2px solid ${appTheme.success.main}`
      : isIncorrect 
        ? `2px solid ${appTheme.error.main}`
        : `2px solid ${appTheme.blue.main}`,
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  display: isVertical ? "flex" : "block",
  flexDirection: isVertical ? "column" : "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  transition: "all 0.2s ease",
}));

// Enhanced feedback container
const FeedbackContainer = styled(Box)(({ isCorrect }) => ({
  padding: "16px",
  marginTop: "12px",
  marginBottom: "20px",
  backgroundColor: isCorrect ? appTheme.success.background : appTheme.error.background,
  borderRadius: "12px",
  border: isCorrect ? `2px solid ${appTheme.success.main}` : `2px solid ${appTheme.error.main}`,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  animation: `${fadeIn} 0.3s ease forwards`,
}));

// New styled components for enhanced UI
const SectionTitle = styled(Typography)({
  marginBottom: "8px",
  fontWeight: "800",
  fontSize: "16px",
  color: "#3C3C3C",
  position: "relative",
});

const ActionButton = styled(Button)(({ variant, color = "primary" }) => ({
  borderRadius: "12px",
  padding: "6px 14px",
  fontWeight: "700",
  fontSize: "14px",
  textTransform: "none",
  letterSpacing: "0.2px",
  transition: "all 0.15s ease-in-out",
  boxShadow: variant === "contained" 
    ? color === "primary" 
      ? "0 4px 0 rgb(17, 3, 166)" 
      : color === "secondary" 
        ? "0 4px 0 #D33131" 
        : "0 4px 0 #777777"
    : "none",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: variant === "contained" 
      ? color === "primary" 
        ? "0 6px 0 rgb(17, 3, 166)" 
        : color === "secondary" 
          ? "0 6px 0 #D33131" 
          : "0 6px 0 #777777"
      : "none",
  },
  "&:active": {
    transform: "translateY(2px)",
    boxShadow: variant === "contained" 
      ? color === "primary" 
        ? "0 2px 0 rgb(17, 3, 166)" 
        : color === "secondary" 
          ? "0 2px 0 #D33131" 
          : "0 2px 0 #777777"
      : "none",
  },
  "&:disabled": {
    backgroundColor: variant === "contained" ? appTheme.neutral.main : "transparent",
    color: variant === "contained" ? appTheme.neutral.darker : appTheme.neutral.dark,
    boxShadow: variant === "contained" ? "0 4px 0 #9E9E9E" : "none",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "6px",
  },
}));

const KeyboardShortcutInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginTop: "12px",
  padding: "8px 12px",
  backgroundColor: appTheme.neutral.light,
  borderRadius: "8px",
  border: `1px solid ${appTheme.neutral.main}`,
  "& svg": {
    marginRight: "6px",
    color: appTheme.neutral.darker,
    fontSize: "16px",
  }
});

// Helper function to shuffle array
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Function to format incorrect answer feedback
const formatIncorrectFeedback = (correctAnswer) => {
  // Split by punctuation and line breaks to create distinct points
  const points = correctAnswer
    .split(/[.!?;\n]+/)
    .map(point => point.trim())
    .filter(point => point.length > 0);
  
  if (points.length <= 1) {
    return correctAnswer;
  }
  
  return points.map((point, index) => `${index + 1}. ${point}`).join("\n");
};

// Token truncation function
const truncateToken = (token, maxLength = 20) => {
  if (token.length <= maxLength) return token;
  return token.substring(0, maxLength - 1) + '…';
};

// Generate a unique ID for tracking token instances
const generateTokenId = (token, index) => {
  return `${token}_${index}`;
};

const ReorderTokensInput = ({ 
  tokens, 
  onSubmitAnswer, 
  waitingToProceed, 
  onProceed, 
  correctAnswer,
  audioData,
  question
}) => {
  // Changed to track tokens with IDs
  const [available, setAvailable] = useState([]);
  const [ordered, setOrdered] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isVerticalLayout, setIsVerticalLayout] = useState(false);
  const [hotkeyMap, setHotkeyMap] = useState({});
  const [reverseHotkeyMap, setReverseHotkeyMap] = useState({});
  const [lastActionTime, setLastActionTime] = useState(Date.now());
  const [showAudio, setShowAudio] = useState(false);
  const [tokenDisplayMap, setTokenDisplayMap] = useState({});
  const [initialShuffleComplete, setInitialShuffleComplete] = useState(false); // Track if initial shuffle is done

  // Check if we should show the audio player
  useEffect(() => {
    if (audioData && question) {
      const questionLowerCase = question.toLowerCase();
      if (questionLowerCase.includes("listen") || questionLowerCase.includes("audio")) {
        setShowAudio(true);
      }
    }
  }, [audioData, question]);

  // Generate randomized hotkeys - modified to handle duplicates
  const generateRandomizedHotkeys = (tokenObjects) => {
    if (!tokenObjects || tokenObjects.length === 0) return { hotkeyMap: {}, reverseMap: {} };
    
    // Create array of possible hotkeys
    const hotkeyChars = [];
    // Numbers 1-9
    for (let i = 1; i <= 9; i++) {
      hotkeyChars.push(String(i));
    }
    // Letters a-z
    for (let i = 0; i < 26; i++) {
      hotkeyChars.push(String.fromCharCode(97 + i));
    }
    
    // Shuffle the hotkey characters
    const shuffledHotkeys = shuffleArray(hotkeyChars.slice(0, tokenObjects.length));
    
    // Create the mapping
    const newHotkeyMap = {};
    const newReverseMap = {};
    
    tokenObjects.forEach((tokenObj, index) => {
      if (index < shuffledHotkeys.length) {
        newHotkeyMap[tokenObj.id] = shuffledHotkeys[index];
        newReverseMap[shuffledHotkeys[index]] = tokenObj.id;
      }
    });
    
    return { hotkeyMap: newHotkeyMap, reverseMap: newReverseMap };
  };

  // Initialize on tokens change with shuffling - ONLY ONCE
  useEffect(() => {
    if (!tokens || tokens.length === 0) return;
    
    // Only shuffle if this is the initial load (not after submission)
    if (initialShuffleComplete && !waitingToProceed) return;
    
    const initialTokens = tokens || [];
    
    // Check if any token exceeds 20 characters or if the average token length is > 12
    const hasLongTokens = initialTokens.some(token => token.length > 30);
    const avgLength = initialTokens.reduce((sum, token) => sum + token.length, 0) / initialTokens.length;
    setIsVerticalLayout(hasLongTokens || avgLength > 15);
    
    // Create display versions of tokens if they're too long
    const newTokenDisplayMap = {};
    // Transform tokens into objects with IDs
    const tokenObjects = initialTokens.map((token, index) => {
      const id = generateTokenId(token, index);
      if (token.length > 20 && !isVerticalLayout) {
        newTokenDisplayMap[id] = truncateToken(token);
      } else {
        newTokenDisplayMap[id] = token;
      }
      return { id, value: token };
    });
    setTokenDisplayMap(newTokenDisplayMap);
    
    // Only shuffle if not already shuffled or if this is a reset
    if (!initialShuffleComplete || waitingToProceed === false) {
      const shuffledTokenObjects = shuffleArray([...tokenObjects]);
      setAvailable(shuffledTokenObjects);
      
      // Generate randomized hotkey mappings
      const { hotkeyMap: newHotkeyMap, reverseMap: newReverseMap } = generateRandomizedHotkeys(tokenObjects);
      setHotkeyMap(newHotkeyMap);
      setReverseHotkeyMap(newReverseMap);
      
      setInitialShuffleComplete(true);
    }
    
    setOrdered([]);
    setFeedback(null);
    setShowFeedback(false);
    setLastActionTime(Date.now());
  }, [tokens, initialShuffleComplete, waitingToProceed]);

  // Update feedback when waiting to proceed changes
  useEffect(() => {
    if (!correctAnswer) return;
    
    const lowerCorrectAnswer = correctAnswer.toLowerCase();
    // Extract token values for comparison
    const userAnswer = ordered.map(token => token.value).join(" ").trim().toLowerCase();
    
    if (waitingToProceed) {
      const isCorrect = isAnswerAccurate(lowerCorrectAnswer, userAnswer);
      
      let formattedCorrectAnswer = correctAnswer;
      if (!isCorrect && isVerticalLayout) {
        formattedCorrectAnswer = formatIncorrectFeedback(correctAnswer);
      }
      
      setFeedback({
        isCorrect,
        userAnswer: ordered.map(token => token.value).join(" "),
        correctAnswer: formattedCorrectAnswer,
      });
      
      setShowFeedback(true);
    } else {
      setShowFeedback(false);
    }
  }, [waitingToProceed, correctAnswer, ordered, isVerticalLayout]);

  // Get hotkey for a token
  const getHotkey = (tokenId) => {
    return hotkeyMap[tokenId] || "";
  };

  // Get display version of a token
  const getDisplayToken = (tokenId, tokenValue) => {
    return tokenDisplayMap[tokenId] || tokenValue;
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (waitingToProceed && e.key === "Enter") {
        onProceed();
        return;
      } else if (e.key === 'Enter') {
        handleSubmit();
      }
      
      if (waitingToProceed) return;

      // Handle number and letter keys using the reverse mapping
      const key = e.key.toLowerCase();
      if (reverseHotkeyMap[key]) {
        const tokenId = reverseHotkeyMap[key];
        const tokenIndex = available.findIndex(t => t.id === tokenId);
        if (tokenIndex !== -1) {
          handleTokenSelect(tokenIndex);
        }
        return;
      }
      
      // Handle backspace to remove last token
      if (e.key === "Backspace" && ordered.length > 0) {
        handleRemoveLastToken();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [available, ordered, waitingToProceed, onProceed, reverseHotkeyMap]);

  const handleTokenSelect = (index) => {
    if (waitingToProceed) return;
    const selectedToken = available[index];
    setOrdered([...ordered, selectedToken]);
    setAvailable(available.filter((_, i) => i !== index));
    setLastActionTime(Date.now());
  };

  const handleRemoveLastToken = () => {
    if (waitingToProceed || ordered.length === 0) return;
    const lastToken = ordered[ordered.length - 1];
    setOrdered(ordered.slice(0, -1));
    setAvailable([...available, lastToken]);
    setLastActionTime(Date.now());
  };

  const handleReset = () => {
    if (!tokens || tokens.length === 0) return;
    
    // Create token objects with IDs
    const tokenObjects = tokens.map((token, index) => ({
      id: generateTokenId(token, index),
      value: token
    }));
    
    // Completely reset with new shuffling and new hotkey assignments
    const shuffledTokenObjects = shuffleArray([...tokenObjects]);
    setAvailable(shuffledTokenObjects);
    
    // Generate new randomized hotkey mappings
    const { hotkeyMap: newHotkeyMap, reverseMap: newReverseMap } = generateRandomizedHotkeys(tokenObjects);
    setHotkeyMap(newHotkeyMap);
    setReverseHotkeyMap(newReverseMap);
    
    setOrdered([]);
    setLastActionTime(Date.now());
  };

  const handleSubmit = () => {
    if (ordered.length) {
      // Extract token values for submission
      onSubmitAnswer(ordered.map(token => token.value));
    }
  };

  return (
    <Box sx={{ 
      mt: 2, 
      p: 2,
      borderRadius: "16px", 
      backgroundColor: "#FFFFFF",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      border: `1px solid ${appTheme.neutral.main}`,
    }}>
      {/* Conditionally render AudioPlayer */}
      {showAudio && audioData && (
        <AudioPlayer audioData={audioData} questionText={question} />
      )}
      
      <SectionTitle variant="h6">
        Your Answer:
      </SectionTitle>
      
      <OrderedContainer 
        isCorrect={showFeedback && feedback?.isCorrect}
        isIncorrect={showFeedback && !feedback?.isCorrect}
        isVertical={isVerticalLayout}
        isEmpty={ordered.length === 0}
      >
        {ordered.map((token, idx) => (
          <TokenCard 
            key={`ordered-${token.id}-${idx}`} 
            selected={true}
            isCorrect={showFeedback && feedback?.isCorrect}
            isIncorrect={showFeedback && !feedback?.isCorrect}
            isVertical={isVerticalLayout}
            sx={{ animationDelay: `${idx * 0.05}s` }}
            title={token.value} // For full text on hover
          >
            {getDisplayToken(token.id, token.value)}
          </TokenCard>
        ))}
        {ordered.length === 0 && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              fontStyle: "italic",
              fontSize: "13px"
            }}
          >
            Select tokens below to form your answer...
          </Typography>
        )}
      </OrderedContainer>

      {showFeedback && (
        <Fade in={showFeedback} timeout={400}>
          <FeedbackContainer isCorrect={feedback?.isCorrect}>
            {feedback?.isCorrect ? (
              <>
                <CheckCircleIcon 
                  sx={{ 
                    color: appTheme.success.dark, 
                    mr: 1.5,
                    fontSize: "26px" // Slightly larger icon
                  }} 
                />
                <Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: "bold",
                    color: appTheme.success.dark,
                    mb: 0.5,
                    fontSize: "18px" // Increased font size
                  }}>
                    Correct!
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: "600", // Made bolder
                    fontSize: "15px" // Increased font size
                  }}>
                    {feedback.userAnswer}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <CancelIcon 
                  sx={{ 
                    color: appTheme.error.dark, 
                    mr: 1.5,
                    fontSize: "26px", // Slightly larger icon
                    alignSelf: "flex-start",
                    mt: 0.5
                  }} 
                />
                <Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: "bold",
                    color: appTheme.error.dark,
                    mb: 0.5,
                    fontSize: "18px" // Increased font size
                  }}>
                    Incorrect
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: "bold", 
                      mt: 1.5,
                      mb: 0.5,
                      color: appTheme.error.dark,
                      fontSize: "16px", // Increased font size
                      position: "relative",
                      display: "inline-block",
                      "&:after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-2px",
                        left: "0",
                        width: "100%",
                        height: "2px",
                        backgroundColor: appTheme.error.dark,
                        borderRadius: "2px",
                      }
                    }}
                  >
                    Correct Answer:
                  </Typography>
                  {isVerticalLayout ? (
                    <Typography 
                      variant="body2"
                      component="pre"
                      sx={{ 
                        whiteSpace: "pre-wrap",
                        fontFamily: "inherit",
                        fontWeight: "600", // Bolder
                        backgroundColor: "rgba(255, 255, 255, 0.5)",
                        p: 1.5,
                        borderRadius: "8px",
                        border: `1px solid ${appTheme.error.light}`,
                        fontSize: "15px" // Increased font size
                      }}
                    >
                      {feedback.correctAnswer}
                    </Typography>
                  ) : (
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: "600", // Bolder
                        backgroundColor: "rgba(255, 255, 255, 0.5)",
                        p: 1.5,
                        borderRadius: "8px",
                        border: `1px solid ${appTheme.error.light}`,
                        fontSize: "15px" // Increased font size
                      }}
                    >
                      {feedback.correctAnswer}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </FeedbackContainer>
        </Fade>
      )}

      <SectionTitle variant="h6" sx={{ mt: 2 }}>
        Available Tokens:
      </SectionTitle>
      
      <Box sx={{ 
        mb: 2,
        display: isVerticalLayout ? "flex" : "block", 
        flexDirection: "column",
        backgroundColor: appTheme.neutral.light,
        p: 1.5,
        borderRadius: "12px",
        border: `1px solid ${appTheme.neutral.main}`
      }}>
        {available.map((token, index) => (
          <TokenCard 
            key={`available-${token.id}-${index}`} 
            onClick={() => handleTokenSelect(index)}
            disabled={waitingToProceed}
            selected={false}
            isVertical={isVerticalLayout}
            sx={{ 
              animationDelay: `${index * 0.05}s`,
              "&:hover": {
                backgroundColor: "#FFFFFF",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 0 rgba(0,0,0,0.15)",
              }
            }}
            title={token.value} // For full text on hover
          >
            {getDisplayToken(token.id, token.value)}
            <Hotkey>{getHotkey(token.id)}</Hotkey>
          </TokenCard>
        ))}
        {available.length === 0 && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              fontStyle: "italic",
              fontSize: "13px"
            }}
          >
            All tokens have been used
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {waitingToProceed ? (
          <ActionButton
            variant="contained"
            color="primary"
            onClick={onProceed}
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              animation: `${pulse} 1.5s infinite`,
              "@keyframes pulse": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.05)" },
                "100%": { transform: "scale(1)" }
              }
            }}
          >
            Continue
          </ActionButton>
        ) : (
          <>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={ordered.length === 0}
              endIcon={<SendIcon />}
            >
              Check
            </ActionButton>
            <ActionButton 
              variant="outlined" 
              color="secondary"
              onClick={handleReset}
              disabled={ordered.length === 0}
              endIcon={<RestartAltIcon />}
            >
              Reset
            </ActionButton>
            {ordered.length > 0 && (
              <ActionButton 
                variant="outlined"
                onClick={handleRemoveLastToken}
                endIcon={<BackspaceIcon />}
                color="inherit"
              >
                Backspace
              </ActionButton>
            )}
          </>
        )}
      </Box>
      
      <KeyboardShortcutInfo>
        <KeyboardIcon fontSize="small" />
        <Typography variant="caption" sx={{ 
          color: appTheme.neutral.darker,
          fontSize: "11px"
        }}>
          Use number keys (1-9) or letters (a-z) as shortcuts to select tokens. Press Backspace to remove the last token.
        </Typography>
      </KeyboardShortcutInfo>
    </Box>
  );
};

export default ReorderTokensInput;